<template>
  <fieldset :data-test="`row-${step.id}`">
    <legend :id="`heading-${step.id}`" class="heading-1 mb-16">
      <span v-if="isTitleWithoutEmphasis">
        {{ step.title }}
      </span>
      <FormattedMessage v-else :definition="stepTitle">
        <template #price>
          <span v-if="step.titlePrice" class="bg-static-info-max">
            {{ i18n.price(step.titlePrice) }}
          </span>
        </template>
        <template #emphasis>
          {{
            typeof step.titleEmphasis === 'string' || !step.titleEmphasis
              ? step.titleEmphasis
              : i18n(step.titleEmphasis)
          }}
        </template>
      </FormattedMessage>
    </legend>

    <!--
      [ToDo CondensedFunnel] side content is only rendered for the grades step.
      The new desktop carousel is rendered here by the parent; the slot default
      is intentionally empty until that lands.
    -->
    <slot :name="`guidance-${step.id}`" />

    <CardGuidance
      v-if="step.guidance && !hideGuidanceCard"
      v-bind="step.guidance"
      :class="{ 'hidden md:flex': GRADES_STEPS.includes(step.id) }"
    />

    <ul
      :aria-labelledby="`heading-${step.id}`"
      class="list-none"
      :class="optionsClasses"
      :data-qa="`heading-${step.id}`"
      role="radiogroup"
    >
      <PickerOption
        v-for="(option, index) in options"
        :key="option.trackingValue"
        :first-picker-id
        :index
        :is-group-loading
        :is-small="isSmallPicker"
        :option
        :step
        @changed="onOptionChanged"
        @click="onOptionClick"
      />

      <li v-if="shouldDisplayToggleButton">
        <LargePicker
          :aria-expanded="displayAllItems"
          class="m-auto h-full"
          :index="options.length"
          :is-small="isSmallPicker"
          :label="toggleLabel"
          @click="toggleDisplayAllItems"
        />
      </li>
    </ul>
  </fieldset>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types/i18n'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import { GRADES_STEPS } from '~/scopes/product-core/components/presentationals/CustomizationFunnel/CustomizationFunnel.constants'
import CardGuidance from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/CardGuidance/CardGuidance.vue'
import LargePicker from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/LargePicker/LargePicker.vue'
import { PICKER_DISPLAYS } from '~/scopes/product-core/components/presentationals/CustomizationFunnelCondensed/CustomizationFunnelCondensed.constants'
import type { CondensedStep } from '~/scopes/product-core/components/presentationals/CustomizationFunnelCondensed/CustomizationFunnelCondensed.types'
import PickerOption from '~/scopes/product-core/components/presentationals/CustomizationFunnelCondensed/components/PickerOption/PickerOption.vue'

import translations from './StepRow.translations'

// [ToDo CondensedFunnel] per-step impression tracking deferred (PPI-8047)
const MAX_PICKER_ITEMS = 6

const props = withDefaults(
  defineProps<{
    step: CondensedStep
    firstPickerId?: string
    hideGuidanceCard?: boolean
  }>(),
  {
    firstPickerId: '',
    hideGuidanceCard: false,
  },
)

defineSlots<Record<`guidance-${string}`, unknown>>()

const displayAllItems = ref(false)
const isGroupLoading = ref(false)
const i18n = useI18n()

const stepTitle = computed(() => {
  return props.step.title as I18nDefinition
})

const shouldDisplayToggleButton = computed(() => {
  return props.step.options.length > MAX_PICKER_ITEMS
})

const maximumAmountOfItemsToShow = computed(() => {
  return MAX_PICKER_ITEMS - 1
})

const remainingItemsCount = computed(() => {
  return props.step.options.length - maximumAmountOfItemsToShow.value
})

const options = computed(() => {
  if (shouldDisplayToggleButton.value && !displayAllItems.value) {
    return props.step.options.slice(0, maximumAmountOfItemsToShow.value)
  }

  return props.step.options
})

const toggleLabel = computed(() => {
  return displayAllItems.value
    ? i18n(translations.showLess)
    : i18n(translations.showMore, {
        remaining: remainingItemsCount.value,
      })
})

const isSmallPicker = computed(() => {
  return props.step.display.display !== PICKER_DISPLAYS.STACKED
})

const isTitleWithoutEmphasis = computed(() => {
  return typeof props.step.title === 'string'
})

/**
 * [CondensedFunnel] Display → layout mapping for the options grid.
 * [ToDo CondensedFunnel] initial utility classes, refine with the final design.
 */
const optionsClasses = computed(() => {
  switch (props.step.display.display) {
    case PICKER_DISPLAYS.HORIZONTAL:
      return 'grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-8'
    case PICKER_DISPLAYS.ZGRID:
      return 'grid grid-cols-1 sm:grid-cols-3 gap-x-12 gap-y-8'
    default:
      return 'grid gap-y-8'
  }
})

function toggleDisplayAllItems() {
  displayAllItems.value = !displayAllItems.value
}
function onOptionClick() {
  isGroupLoading.value = true
}
function onOptionChanged() {
  isGroupLoading.value = false
}
</script>
